/**
 * Alle Texte von Coco an einer Stelle (Inhaber 20.10.2026: „die Leute müssen besser abgeholt werden“). Ausgetauscht wird nur hier.
 * Stand: endgültige Texte des Vertriebs vom 20.10.2026 (vertrieb/Coco_Texte_Einfuehrung_2026-10-20.md, Teile A bis E), gegen die
 * echte Funktion geprüft; wo ein Satz so nicht stimmte, ist er angepasst und mit „geprüft:“ kommentiert. Begriffe wie im Portal
 * (docs/COCO.md: „Beweiskette“, „Agentur“). Schweizer Schreibung (ß → ss) macht t() im Agentur-Portal selbst (K-284).
 *
 *  1  Rundgang je Rolle: Stationen (Titel, Text) für Agentur-Admin (A), Team (B) und Creator-Portal (C).
 *  2  Knöpfe und Hinweise der Sprechblase, des Coco-Symbols und der Wege zurück (Hilfe, Konto); leere Listen (E).
 *  3  Seitenhilfe „Coco erklärt“ je Hauptseite der Agentur (D; bisher einfuehrung-texte.ts).
 *  4  Seitenhilfe im Creator-Portal (bisher creator/einstieg.ts).
 *
 * Welche Station auf welche Seite führt und welchen Knopf sie hervorhebt, steht in coco-rundgang.ts; diese Datei hat nur Texte.
 */
export type Tr = (de: string, en: string) => string;

// ── 1  Rundgang ──────────────────────────────────────────────────────────────

export type StationId =
  | "willkommen" | "creator" | "daten" | "marke" | "kooperation" | "vertrag" | "rechnung" | "auszahlung" | "abschluss"
  | "s-willkommen" | "s-daten" | "s-marke" | "s-deal" | "s-vertrag" | "s-rechnung" | "s-abschluss"
  | "team-willkommen" | "team-kooperationen" | "team-vertraege" | "team-chat"
  | "c-willkommen" | "c-deals" | "c-rechnung" | "c-vertraege" | "c-chat" | "c-profil";

/** hinweis: kleine Zeile unter dem Text (K-573: die Vertrauens-Aussage in Schritt 1, damit der Text bei zwei Sätzen bleibt) */
export interface StationText { titel: string; text: string; hinweis?: string }

/**
 * Text einer Station. agency: Name der Agentur (Creator-Portal); freigabeAb: Tarif, ab dem die Content-Freigabe per Link geht, wenn
 * der eigene ihn nicht hat (K-557, dann „(ab Team)“ am Satz); beispiel: Abschluss bietet Beispieldaten an; beispielGewaehlt: in
 * Schritt 1 wurden Beispieldaten angelegt, der Abschluss fragt „behalten oder entfernen?“; beispielDa (K-595): es gibt Beispieldaten
 * (der Beispiel-Creator steht schon in der Liste, Schritt 2 sagt das); leer (K-573): die Seite zeigt statt des
 * Knopfs den leeren Bereich (data-coco-leer), der Text sagt, was dort später erscheint.
 *
 * K-573 (Inhaber 21.10.2026): je Schritt ein Nutzen-Satz und eine Handlung, kurz, du-Form, ohne Gedankenstriche.
 */
export function stationText(id: StationId, t: Tr, opts: { agency?: string; freigabeAb?: string | null; beispiel?: boolean; beispielGewaehlt?: boolean; beispielDa?: boolean; liste?: boolean; leer?: boolean } = {}): StationText {
  // K-558 a: ist die Startliste erledigt oder ausgeblendet, verweisen Hi und Abschluss nicht auf sie
  const liste = opts.liste !== false;
  const leer = !!opts.leer;
  const ab = opts.freigabeAb ? t(` (ab ${opts.freigabeAb})`, ` (from ${opts.freigabeAb})`) : "";
  // geprüft: „Coco erklärt“ ist seit G4 (21.10.2026) ein Knopf oben im Seitenkopf der Hauptseiten; unten rechts liegt der Chat
  const fragen = t("Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“.", "For questions about a page, click “Coco explains” at the top.");
  switch (id) {
    // A  Agentur-Admin
    case "willkommen": return {
      titel: t("Hi, ich bin Coco!", "Hi, I'm Coco!"),
      text: t("Verträge, Freigaben, Rechnungen und Auszahlungen an einem Ort, statt Excel und Mail-Pingpong. In einer Minute zeige ich dir, wie es geht.",
        "Contracts, approvals, invoices and payouts in one place, instead of spreadsheets and email ping-pong. In one minute I'll show you how it works."),
      // K-557: Skepsis nehmen; geprüft: Marken und Creator bekommen Mails erst beim Senden bzw. Einladen, Beispieldaten senden nichts
      hinweis: t("Keine Sorge: Marken und Creator bekommen erst etwas, wenn du es abschickst oder sie einlädst.",
        "Don't worry: brands and creators only get something once you send it or invite them."),
    };
    case "creator": return opts.beispielDa || opts.beispielGewaehlt ? {
      titel: t("Zuerst deine Creator", "Start with your creators"),
      // K-595: mit Beispieldaten steht schon ein Beispiel-Creator in der Liste; „Leg den ersten an“ passte dann nicht
      text: t("Hier hast du alle Creator, die du managst, mit Profil, Konditionen und Zugang. Der Beispiel-Creator zeigt, wie das aussieht; deinen ersten echten legst du mit „Creator anlegen“ an, der Name reicht.",
        "All the creators you manage, with profile, terms and access. The sample creator shows what it looks like; add your first real one with “Add creator”, a name is enough."),
    } : {
      titel: t("Zuerst deine Creator", "Start with your creators"),
      // geprüft: „Creator anlegen“ fragt Name und E-Mail; „Ohne Einladung anlegen“ braucht nur den Namen (kein Instagram-Feld)
      text: t("Hier hast du alle Creator, die du managst, mit Profil, Konditionen und Zugang. Leg den ersten mit „Creator anlegen“ an, der Name reicht.",
        "All the creators you manage, with profile, terms and access. Add your first one with “Add creator”; a name is enough."),
    };
    case "daten": return {
      titel: t("Deine Firmendaten", "Your company details"),
      // geprüft: Firmenname, Anschrift, Steuernummer und Bankverbindung (BankPanel) stehen auf Verträgen bzw. Rechnungen
      text: t("Einmal eingetragen, stehen Name, Anschrift, Steuernummer und Bankverbindung auf jedem Vertrag und jeder Rechnung. Fang hier mit deinem Firmennamen an.",
        "Enter them once and your name, address, tax number and bank details appear on every contract and invoice. Start with your company name here."),
    };
    case "marke": return {
      titel: t("Marken sind deine Kunden", "Brands are your customers"),
      text: t("Firma, Ansprechpartner und Rechnungsadresse trägst du einmal ein, danach wählst du die Marke in jeder Kooperation nur noch aus. Leg sie mit „Marke anlegen“ an.",
        "Enter company, contact and billing address once, then just pick the brand in every collaboration. Add it with “Add brand”."),
    };
    // K-595: die Seite der Agentur heißt „Kooperationen“; im Creator-Konto (s-deal) bleibt es der Deal
    case "kooperation": return leer ? {
      titel: t("Hier läuft jede Kooperation", "Every deal lives here"),
      // K-573: ohne Creator zeigt die Seite „Lege zuerst einen Creator an“; Coco erklärt diesen Bereich statt auf „Neue Kooperation“ zu zeigen
      text: t(`Sobald dein erster Creator angelegt ist, verbindest du hier Creator und Marke, daraus entstehen Vertrag, Rechnung und Auszahlung. Entwürfe gibt die Marke über einen Link frei${ab}.`,
        `Once your first creator is added, you connect creator and brand here; contract, invoice and payout build on it. The brand approves drafts via a link${ab}.`),
    } : {
      titel: t("Hier läuft jede Kooperation", "Every deal lives here"),
      // geprüft: der Knopf heißt auf Englisch „New deal“; Content-Freigabe per Link ist module_brand_review (K-557: Satz statt eigener Station)
      text: t(`Mit „Neue Kooperation“ verbindest du Creator und Marke, daraus entstehen Vertrag, Rechnung und Auszahlung. Entwürfe gibt die Marke über einen Link frei${ab}.`,
        `Use “New deal” to connect creator and brand; contract, invoice and payout build on it. The brand approves drafts via a link${ab}.`),
    };
    case "vertrag": return {
      titel: t("Verträge aus dem Muster", "Contracts from templates"),
      // geprüft: im Portal heißt das Nachweisprotokoll „Beweiskette“
      text: t("Muster wählen, anpassen und per Link digital unterschreiben lassen, mit Beweiskette. Starte mit „Vertrag erstellen“.",
        "Pick a template, adjust it and get it signed digitally via link, with an audit trail. Start with “Create contract”."),
    };
    case "rechnung": return leer ? {
      titel: t("Rechnungen ohne Abtippen", "Invoices without retyping"),
      text: t("Hier erscheinen deine Rechnungen an Marken, sobald die erste Kooperation läuft. Sie entstehen direkt daraus, Beträge und Angaben übernimmt das Portal.",
        "Your invoices to brands appear here once your first collaboration is running. They are built straight from it; the portal fills in amounts and details."),
    } : {
      titel: t("Rechnungen ohne Abtippen", "Invoices without retyping"),
      // geprüft: Mahnungen verschickt das Team selbst; der tägliche Prüflauf legt bei überfälligen Rechnungen Aufgaben an
      text: t("Mit „Rechnung erstellen“ entsteht die Rechnung an die Marke direkt aus der Kooperation. Ist sie überfällig, erinnert dich das Portal an die Zahlungserinnerung.",
        "“Create invoice” builds the invoice to the brand straight from the collaboration. If it is overdue, the portal reminds you to send a payment reminder."),
    };
    case "auszahlung": return leer ? {
      titel: t("Geld an deine Creator", "Paying your creators"),
      // K-573: ohne fällige Anteile ist „Auszahlungslauf erstellen“ grau; Coco erklärt den Bereich „Offene Auszahlungen“
      text: t("Hier erscheinen die Anteile deiner Creator, sobald sie fällig sind. Dann zahlst du sie gesammelt in einem Auszahlungslauf aus, einzeln geht es an der Rechnung.",
        "Your creators' shares appear here once they are due. Then you pay them out together in a payout run; one by one works on the invoice."),
    } : {
      titel: t("Geld an deine Creator", "Paying your creators"),
      // geprüft: die Seite bündelt fällige Anteile zu einem Lauf; einzeln zahlst du an der Rechnung aus; „erst nach Zahlung der Marke“ ist eine Einstellung
      text: t("Fällige Anteile zahlst du hier gesammelt in einem Auszahlungslauf aus, samt Bankdatei. Einzeln geht es direkt an der Rechnung.",
        "Pay out due shares here together in a payout run, bank file included. One by one works directly on the invoice."),
    };
    case "abschluss": return liste ? {
      titel: t("Das war’s schon!", "That's it!"),
      // K-573: auf der Übersicht neben der Liste; Knopf „Mit dem nächsten Schritt weitermachen“ bzw. „behalten oder entfernen?“
      text: opts.beispielGewaehlt
        ? t("Diese Liste führt dich jetzt Schritt für Schritt zum ersten echten Deal. Die Beispieldaten behalten oder gleich entfernen?",
          "This list now guides you step by step to your first real deal. Keep the sample data or remove it now?")
        : opts.beispiel
          ? t("Diese Liste führt dich jetzt Schritt für Schritt zum ersten echten Deal, oder du schaust dir erst alles mit Beispieldaten an. Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“.",
            "This list now guides you step by step to your first real deal, or explore everything with sample data first. For questions about a page, click “Coco explains” at the top.")
          : t("Diese Liste führt dich jetzt Schritt für Schritt zum ersten echten Deal. Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“.",
            "This list now guides you step by step to your first real deal. For questions about a page, click “Coco explains” at the top."),
      hinweis: opts.beispielGewaehlt ? fragen : undefined,
    } : {
      // K-558 a: ohne Startliste (erledigt oder ausgeblendet); geprüft: Hilfe & Support hat „Rundgang starten“
      titel: t("Du bist startklar", "You're all set"),
      text: opts.beispielGewaehlt
        ? t("Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“. Die Beispieldaten behalten oder gleich entfernen?",
          "For questions about a page, click “Coco explains” at the top. Keep the sample data or remove it now?")
        : opts.beispiel
          ? t("Wenn du magst, schau dir erst alles mit Beispieldaten an. Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“.",
            "If you like, explore everything with sample data first. For questions about a page, click “Coco explains” at the top.")
          : t("Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“. Unter Hilfe & Support startest du den Rundgang neu.",
            "For questions about a page, click “Coco explains” at the top. You can restart the tour under Help & support."),
    };
    // S  Eigenes Creator-Konto (K-557)
    case "s-willkommen": return {
      titel: t("Hi, ich bin Coco!", "Hi, I'm Coco!"),
      text: t("Deals, Verträge, Freigaben und Rechnungen an einem Ort, statt Excel und Mail-Pingpong. In einer Minute zeige ich dir, wie es geht.",
        "Deals, contracts, approvals and invoices in one place, instead of spreadsheets and email ping-pong. In one minute I'll show you how it works."),
      hinweis: t("Keine Sorge: Marken bekommen erst etwas, wenn du es abschickst.", "Don't worry: brands only get something once you send it."),
    };
    case "s-daten": return {
      titel: t("Deine Angaben", "Your details"),
      // geprüft: Steuerstatus (Kleinunternehmer) und Land stehen auf derselben Seite; die Steuer auf Rechnungen folgt daraus
      text: t("Einmal eingetragen, stehen Name, Anschrift, Steuernummer und Bankverbindung auf jedem Vertrag und jeder Rechnung. Wähl hier auch Land und Steuerstatus, dann setzt das Portal die Steuer selbst.",
        "Enter them once and your name, address, tax number and bank details appear on every contract and invoice. Also choose country and tax status here; the portal then sets the tax itself."),
    };
    case "s-marke": return {
      titel: t("Marken sind deine Kunden", "Brands are your customers"),
      text: t("Firma, Ansprechpartner und Rechnungsadresse trägst du einmal ein, danach wählst du die Marke in jedem Deal nur noch aus. Leg sie mit „Marke anlegen“ an.",
        "Enter company, contact and billing address once, then just pick the brand in every deal. Add it with “Add brand”."),
    };
    case "s-deal": return {
      titel: t("Hier läuft jeder Deal", "Every deal lives here"),
      // geprüft: im Creator-Konto heißt der Knopf „Neuer Deal“
      text: leer
        ? t(`Hier erscheinen deine Deals mit Marke, Leistungen und Honorar, daraus entstehen Vertrag und Rechnung. Entwürfe gibt die Marke über einen Link frei${ab}.`,
          `Your deals with brand, deliverables and fee appear here; contract and invoice build on them. The brand approves drafts via a link${ab}.`)
        : t(`Mit „Neuer Deal“ hältst du Marke, Leistungen und Honorar an einem Ort fest, daraus entstehen Vertrag und Rechnung. Entwürfe gibt die Marke über einen Link frei${ab}.`,
          `Use “New deal” to keep brand, deliverables and fee in one place; contract and invoice build on it. The brand approves drafts via a link${ab}.`),
    };
    case "s-vertrag": return {
      titel: t("Verträge aus dem Muster", "Contracts from templates"),
      text: t("Muster wählen, anpassen und von der Marke per Link digital unterschreiben lassen, mit Beweiskette. Starte mit „Vertrag erstellen“.",
        "Pick a template, adjust it and have the brand sign it digitally via link, with an audit trail. Start with “Create contract”."),
    };
    case "s-rechnung": return {
      titel: t("Rechnung an die Marke", "Invoice to the brand"),
      // geprüft: Steuer nach eigenem Land/Steuerstatus und Land der Marke (studioGuideText „rechnungen“); „Zahlung erinnern“ an der Rechnung
      text: leer
        ? t("Hier erscheinen deine Rechnungen, sobald der erste Deal läuft. Sie entstehen direkt daraus, die Steuer setzt das Portal nach deinen Angaben und dem Land der Marke.",
          "Your invoices appear here once your first deal is running. They are built straight from it; the portal sets the tax from your details and the brand's country.")
        : t("Mit „Rechnung erstellen“ entsteht die Rechnung direkt aus dem Deal, die Steuer setzt das Portal selbst. Zahlt die Marke nicht, erinnerst du sie mit einem Klick.",
          "“Create invoice” builds the invoice straight from the deal and the portal sets the tax. If the brand does not pay, remind it with one click."),
    };
    case "s-abschluss": return liste ? {
      titel: t("Das war’s schon!", "That's it!"),
      text: t("Diese Liste führt dich jetzt Schritt für Schritt zu deinem ersten Deal. Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“.",
        "This list now guides you step by step to your first deal. For questions about a page, click “Coco explains” at the top."),
    } : {
      titel: t("Du bist startklar", "You're all set"),
      text: t("Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“. Unter Hilfe & Support startest du den Rundgang neu.",
        "For questions about a page, click “Coco explains” at the top. You can restart the tour under Help & support."),
    };
    // B  Team (Manager ohne Admin)
    case "team-willkommen": return {
      titel: t("Hi, ich bin Coco!", "Hi, I'm Coco!"),
      text: t("Willkommen im Team! Auf der Übersicht siehst du, was in deinen Kooperationen gerade ansteht.",
        "Welcome to the team! The overview shows what's coming up in your collaborations."),
    };
    case "team-kooperationen": return {
      titel: t("Deine Deals", "Your deals"),
      // geprüft: die Liste zeigt Status, Monate und nächste Frist, keinen „nächsten Schritt“
      text: t("Alle Kooperationen, die du betreust, mit Status, Monaten und nächster Frist. Ein Klick auf eine Zeile öffnet alle Details.",
        "All collaborations you manage, with status, months and next deadline. Click a row to open all details."),
    };
    case "team-vertraege": return {
      titel: t("Verträge im Blick", "Contracts at a glance"),
      text: t("Hier siehst du, welcher Vertrag unterschrieben ist und wo noch eine Unterschrift fehlt.", "See which contracts are signed and where a signature is still missing."),
    };
    case "team-chat": return {
      titel: t("Direkt mit den Creatorn", "Talk to your creators"),
      text: t("Schreib deinen Creatorn hier, statt Infos über WhatsApp zu verteilen. Bei Fragen zu einer Seite klickst du oben auf „Coco erklärt“.",
        "Message your creators here instead of spreading info across WhatsApp. For questions about a page, click “Coco explains” at the top."),
    };
    // C  Creator-Portal
    case "c-willkommen": return {
      titel: t("Hi, ich bin Coco!", "Hi, I'm Coco!"),
      // geprüft: die Übersicht zeigt nächste Schritte, Deals und dein Geld, keine Termine (die stehen im Kalender)
      text: opts.agency
        ? t(`Hier siehst du deine nächsten Schritte, Deals und Auszahlungen von ${opts.agency} an einem Ort.`, `Your next steps, deals and payouts from ${opts.agency}, all in one place.`)
        : t("Hier siehst du deine nächsten Schritte, Deals und Auszahlungen an einem Ort.", "Your next steps, deals and payouts, all in one place."),
    };
    case "c-deals": return {
      titel: t("Deine Kooperationen", "Your collaborations"),
      text: t("Jede Kooperation mit Briefing, Fristen und Status. Hier reichst du auch deine Entwürfe ein.",
        "Each collaboration with briefing, deadlines and status. You also submit your drafts here."),
    };
    case "c-rechnung": return {
      titel: t("Rechnung in Sekunden", "Invoice in seconds"),
      // geprüft: der Assistent erzeugt die Rechnung aus Kooperation und Monat (vorgewählt, wenn die Agentur sie angefordert hat) oder nimmt eine eigene Datei;
      // ein Zahltag steht nicht an jeder Rechnung, der Stand bis zur Auszahlung schon
      text: t("Lade deine Rechnung hoch oder lass sie im Assistenten aus der Kooperation erzeugen. Du siehst immer, wo sie steht, bis zur Auszahlung.",
        "Upload your invoice or let the assistant create it from the collaboration. You always see where it stands, up to the payout."),
    };
    case "c-vertraege": return {
      titel: t("Unterschreiben per Klick", "Sign with a click"),
      text: t("Deine Verträge liegen hier, du unterschreibst sie digital und hast sie jederzeit griffbereit.", "Your contracts live here, sign them digitally and keep them at hand."),
    };
    case "c-chat": return {
      titel: t("Kurzer Draht", "Direct line"),
      // geprüft: im Portal heißt das Gegenüber „Agentur“
      text: t("Hier schreibst du direkt mit deiner Agentur.", "Message your agency directly here."),
    };
    case "c-profil": return {
      titel: t("Damit das Geld ankommt", "So the money arrives"),
      text: t("Prüf deine Bankdaten und Steuerangaben, dann laufen deine Auszahlungen ohne Rückfragen.", "Check your bank and tax details so your payouts run without questions."),
    };
  }
}

// ── 2  Knöpfe und Hinweise ───────────────────────────────────────────────────

export function cocoUiText(t: Tr) {
  return {
    eyebrow: t("Rundgang mit Coco", "Tour with Coco"),
    schritt: (n: number, von: number) => t(`Schritt ${n} von ${von}`, `Step ${n} of ${von}`),
    weiter: t("Weiter", "Next"),
    zurueck: t("Zurück", "Back"),
    ueberspringen: t("Überspringen", "Skip"),
    fertig: t("Fertig", "Done"),
    // geprüft: die Seite heißt auf Englisch „Quick start“
    zumSchnellstart: t("Schnellstart starten", "Start quick start"),
    // K-557: Abschluss führt zum nächsten offenen Punkt der Startliste
    naechsterSchritt: t("Mit dem nächsten Schritt weitermachen", "Continue with the next step"),
    zurUebersicht: t("Zur Übersicht", "To the overview"),
    beispieldaten: t("Zeig’s mir mit Beispieldaten", "Show me with sample data"),
    beispieldatenLaeuft: t("Beispieldaten werden angelegt …", "Creating sample data …"),
    // K-573: Wahl in Schritt 1 (Standard „Mit Beispieldaten zeigen“) und Frage am Ende
    mitBeispiel: t("Mit Beispieldaten zeigen", "Show with sample data"),
    ohneBeispiel: t("Ohne Beispiele", "Without samples"),
    beispielWahlHinweis: t("Beispieldaten sind erfunden, senden nichts und lassen sich am Ende mit einem Klick entfernen.",
      "Sample data is made up, sends nothing and can be removed with one click at the end."),
    beispielBehalten: t("Beispieldaten behalten", "Keep sample data"),
    beispielEntfernen: t("Beispieldaten entfernen", "Remove sample data"),
    beispielEntfernenLaeuft: t("Wird entfernt …", "Removing …"),
    beispielEntfernt: t("Beispieldaten entfernt. Deine eigenen Daten bleiben.", "Sample data removed. Your own data stays."),
    beispieldatenDa: t("Beispieldaten sind da. Sie senden nichts und zählen nicht in deinen Tarif.", "Sample data is ready. It sends nothing and does not count towards your plan."),
    // Seitenhilfe und Coco-Symbol
    seitenEyebrow: t("Coco erklärt", "Coco explains"),
    lugt: t("Coco erklärt diese Seite", "Coco explains this page"),
    verstanden: t("Verstanden", "Got it"),
    ausschalten: t("Einführung ausschalten", "Switch off introduction"),
    rundgangStarten: t("Rundgang starten", "Start tour"),
    // Hilfe & Support (Agentur) und Konto (Creator-Portal)
    hilfeTitel: t("Rundgang mit Coco", "Tour with Coco"),
    hilfeText: t("Coco zeigt dir in etwa einer Minute die wichtigsten Stellen im Portal. Auf den Hauptseiten erklärt Coco außerdem, was dort passiert; der Knopf „Coco erklärt“ oben im Seitenkopf holt die Erklärung zurück.",
      "In about a minute Coco shows you the key places in the portal. On the main pages Coco also explains what happens there; the “Coco explains” button at the top of the page header brings the explanation back."),
    seitenhilfeAn: t("Seitenhilfe wieder einschalten", "Switch page help back on"),
    seitenhilfeAnGemacht: t("Die Seitenhilfe erscheint wieder.", "The page help will show again."),
  };
}

// ── 3  Seitenhilfe der Agentur ───────────────────────────────────────────────

export type AgenturGuide = "overview" | "kooperationen" | "anfragen" | "vertraege" | "rechnungen" | "auszahlungen" | "creator" | "marken" | "content" | "kalender" | "nachrichten"
  | "kennzahlen" | "einstellungen" | "pitch" | "hub";

/** Beschriftung des Knopfs „was legst du als Erstes an“, wo es einen Weg gibt (Ziel und Rechte in coco-rundgang.ts) */
export function seitenKnopf(key: AgenturGuide, t: Tr, opts: { studio: boolean }): string | null {
  switch (key) {
    case "overview": return t("Schnellstart öffnen", "Open quick start");
    case "creator": return opts.studio ? null : t("Creator anlegen", "Add creator");
    case "kooperationen": return opts.studio ? t("Deal anlegen", "Create deal") : t("Kooperation anlegen", "Create deal");
    case "anfragen": return t("Anfrage anlegen", "Create inquiry");
    case "vertraege": return t("Vertrag erstellen", "Create contract");
    case "rechnungen": return t("Rechnung erstellen", "Create invoice");
    case "einstellungen": return t("Firmendaten öffnen", "Open company details");
    default: return null;
  }
}

/** E  Leere Listen: ein Satz, dazu der Knopf „Zeig’s mir mit Beispieldaten“ (nur wo es Beispieldaten dafür gibt) */
export function leereListe(key: AgenturGuide, t: Tr): string | null {
  switch (key) {
    case "kooperationen": return t("Noch keine Kooperation. Leg deinen ersten Deal an oder schau dir an, wie eine fertige Kooperation aussieht.", "No collaborations yet. Add your first deal or see what a finished collaboration looks like.");
    case "creator": return t("Noch keine Creator. Leg einen an oder schau dir ein Beispiel-Profil an.", "No creators yet. Add one or look at a sample profile.");
    case "vertraege": return t("Noch kein Vertrag. Starte mit einer Mustervorlage oder sieh dir einen Beispielvertrag an.", "No contracts yet. Start from a template or look at a sample contract.");
    case "rechnungen": return t("Noch keine Rechnung. Erstell sie direkt aus einer Kooperation oder sieh dir eine Beispielrechnung an.", "No invoices yet. Create one from a collaboration or look at a sample invoice.");
    default: return null;
  }
}

export interface AgenturGuideText { title: string; text: string; bullets: string[] }

/** D  Seitenhilfe: was passiert hier (Text) und was legst du als Erstes an (erster Hinweis) */
export function agenturGuideText(key: AgenturGuide, t: Tr): AgenturGuideText {
  switch (key) {
    // Übersicht: nicht Teil der Vertriebstexte, bleibt wie seit 02.10.2026
    case "overview": return {
      title: t("Willkommen bei CreatorOffice", "Welcome to CreatorOffice"),
      text: t("Die Übersicht zeigt Umsatz, Agenturanteil und was heute zu tun ist.", "The overview shows revenue, agency share and what needs doing today."),
      bullets: [
        t("Offene Aufgaben stehen oben, zum Beispiel Rechnungen, die auf deine Freigabe warten.", "Open tasks are at the top, for example invoices waiting for your approval."),
        t("Links findest du alle Bereiche, von Anfragen bis Auszahlungen.", "On the left you find every area, from inquiries to payouts."),
        t("Die Glocke oben rechts meldet, was Creator und Marken gerade tun.", "The bell at the top right shows what creators and brands are doing right now."),
      ],
    };
    case "creator": return {
      title: t("Deine Creator", "Your creators"),
      text: t("Hier verwaltest du dein Roster mit Profil, Provision und Kanälen.", "Manage your roster with profile, commission and channels."),
      bullets: [
        // geprüft: „Ohne Einladung anlegen“ braucht nur den Namen; ein Handle-Feld hat das Formular nicht
        t("Leg zuerst einen Creator an, der Name reicht.", "Add a creator first, a name is enough."),
        t("Lade Creator ins Portal ein, dann sehen sie ihre Deals selbst.", "Invite creators to the portal so they see their deals themselves."),
        t("Die Provision legst du je Creator fest.", "Set the commission per creator."),
      ],
    };
    case "hub": return {
      title: t("Hub", "Hub"),
      text: t("Alles auf einen Blick: je Marke, Kampagne oder Creator der Stand von Vertrag, Content und Rechnung.", "Everything at a glance: the status of contract, content and invoice per brand, campaign or creator."),
      bullets: [
        t("Oben rechts wechselst du den Blickwinkel: nach Marke, nach Kampagne oder nach Creator.", "Switch the view at the top right: by brand, by campaign or by creator."),
        t("Ein Klick auf den Zähler „hängen“ zeigt nur, wo etwas rot ist, zum Beispiel ein überfälliger Beitrag.", "Clicking the “stuck” counter shows only what is red, for example an overdue post."),
        t("Ein Klick auf eine Pille springt direkt zum Vertrag, Beitrag oder zur Rechnung.", "Click a pill to jump straight to the contract, post or invoice."),
      ],
    };
    case "kooperationen": return {
      title: t("Kooperationen", "Collaborations"),
      text: t("Jeder Deal mit einer Marke, von der Anfrage bis zur Auszahlung.", "Every brand deal from request to payout."),
      bullets: [
        t("Leg zuerst eine laufende Kooperation an.", "Start with one running collaboration."),
        t("Die Marke legst du direkt im selben Fenster an.", "Add the brand in the same window."),
        t("Fristen erscheinen automatisch im Kalender.", "Deadlines show up in the calendar automatically."),
      ],
    };
    case "anfragen": return {
      title: t("Anfragen", "Requests"),
      text: t("Hier landen Anfragen von Marken, bevor daraus eine Kooperation wird.", "Brand requests land here before they become a collaboration."),
      bullets: [
        t("Leg zuerst eine eingegangene Anfrage an.", "Add an incoming request first."),
        // geprüft: „Angebot erstellen“ an der Anfrage, nach der Zusage „Zusage → Kooperation anlegen“; zwei Schritte, nicht ein Klick
        t("Daraus erstellst du ein Angebot; nach der Zusage legst du mit „Zusage → Kooperation anlegen“ die Kooperation an.", "Create an offer from it; once accepted, “Accepted → create deal” creates the collaboration."),
      ],
    };
    case "pitch": return {
      title: t("Pitch-Listen", "Pitch lists"),
      // geprüft: die Auswahl geht als persönlicher Link an die Marke
      text: t("Stell Creator für eine Marke zusammen und schick die Auswahl als persönlichen Link.", "Put together creators for a brand and send the selection as a personal link."),
      bullets: [
        t("Leg zuerst eine Liste für eine Marke an.", "Create a list for one brand first."),
        // geprüft: Kennzahlen pflegst du im Media-Kit; Werte aus dem verbundenen Konto nur mit Zustimmung des Creators
        t("Kennzahlen kommen aus dem Media-Kit; Werte aus dem verbundenen Konto nur, wenn der Creator der Weitergabe zugestimmt hat.", "Metrics come from the media kit; values from the connected account only if the creator has consented to sharing."),
      ],
    };
    case "vertraege": return {
      title: t("Verträge", "Contracts"),
      text: t("Alle Verträge mit Status, von Entwurf bis unterschrieben.", "All contracts with status, from draft to signed."),
      bullets: [
        t("Starte mit einem Mustervertrag.", "Start with a template."),
        t("Recht und Gerichtsstand kommen aus deinen Firmendaten.", "Governing law and venue come from your company details."),
        // geprüft: im Portal heißt das Nachweisprotokoll „Beweiskette“
        t("Unterschrieben wird per Link, mit Beweiskette.", "Signing happens via link, with an audit trail."),
      ],
    };
    case "rechnungen": return {
      title: t("Rechnungen", "Invoices"),
      text: t("Rechnungen an Marken, offene Posten und Zahlungserinnerungen.", "Invoices to brands, open items and payment reminders."),
      bullets: [
        t("Erstell die erste Rechnung direkt aus einer Kooperation.", "Create your first invoice from a collaboration."),
        t("Hinterleg vorher deine Firmen- und Bankdaten.", "Add your company and bank details first."),
        // geprüft: Erinnerungen laufen nicht automatisch; der tägliche Prüflauf legt Aufgaben an, verschickt wird über „Zahlung erinnern“
        t("Bei überfälligen Rechnungen legt dir das Portal eine Aufgabe an; die Erinnerung schickst du über „Zahlung erinnern“.", "For overdue invoices the portal creates a task for you; you send the reminder via “Send payment reminder”."),
      ],
    };
    case "auszahlungen": return {
      title: t("Auszahlungen", "Payouts"),
      // geprüft: fällig nach Frist oder, wenn so eingestellt, erst nach Zahlung der Marke
      text: t("Hier zahlst du deine Creator aus, sobald ihr Anteil fällig ist, auf Wunsch erst nach Zahlung der Marke.", "Pay your creators here once their share is due, optionally only after the brand has paid."),
      bullets: [
        t("Prüf zuerst die Bankdaten deiner Creator.", "Check your creators' bank details first."),
        t("Sammle mehrere Auszahlungen in einem Lauf.", "Bundle several payouts in one run."),
      ],
    };
    case "content": return {
      title: t("Content", "Content"),
      text: t("Entwürfe deiner Creator und die Freigabe durch die Marke.", "Your creators' drafts and the brand's approval."),
      bullets: [
        t("Lass deinen Creator den ersten Entwurf an der Kooperation einreichen.", "Have your creator submit the first draft on the collaboration."),
        t("Schick der Marke den Freigabe-Link.", "Send the brand the approval link."),
      ],
    };
    case "kalender": return {
      title: t("Kalender", "Calendar"),
      text: t("Fristen, Veröffentlichungen und Termine für Team und Creator.", "Deadlines, publications and dates for team and creators."),
      bullets: [
        t("Fristen aus Kooperationen erscheinen hier automatisch.", "Collaboration deadlines appear here automatically."),
        // geprüft: „Kalender abonnieren“ gibt einen Abo-Link (ICS) für jede Kalender-App
        t("Über „Kalender abonnieren“ holst du ihn in deinen Handy-Kalender.", "Use “Subscribe to calendar” to get it into your phone calendar."),
      ],
    };
    case "kennzahlen": return {
      title: t("Kennzahlen", "Metrics"),
      text: t("Umsatz, Provision und offene Beträge nach Creator, Marke und Monat.", "Revenue, commission and open amounts by creator, brand and month."),
      bullets: [
        // geprüft: die Zahlen kommen aus Rechnungen und Buchungen, nicht schon aus der angelegten Kooperation
        t("Die Zahlen füllen sich, sobald du die ersten Kooperationen abrechnest.", "Numbers fill up once you invoice your first collaborations."),
        t("Filtere nach Zeitraum oder Creator.", "Filter by period or creator."),
      ],
    };
    case "nachrichten": return {
      title: t("Chat", "Chat"),
      text: t("Nachrichten mit deinen Creatorn, direkt an der Kooperation oder allgemein.", "Messages with your creators, on a collaboration or in general."),
      bullets: [
        t("Lade zuerst einen Creator ins Portal ein.", "Invite a creator to the portal first."),
      ],
    };
    case "marken": return {
      title: t("Marken", "Brands"),
      text: t("Deine Marken mit Ansprechpartnern, Rechnungsdaten und Zahlungsziel.", "Your brands with contacts, billing details and payment terms."),
      bullets: [
        t("Leg Marken am einfachsten direkt in der Kooperation an.", "The easiest way to add a brand is inside a collaboration."),
        t("Hinterleg die Rechnungsadresse, dann stimmt jede Rechnung.", "Add the billing address so every invoice is right."),
      ],
    };
    case "einstellungen": return {
      title: t("Einstellungen", "Settings"),
      text: t("Firmendaten, Bankverbindung, Team, Abo und Branding.", "Company details, bank account, team, plan and branding."),
      bullets: [
        t("Ergänze zuerst Firmen- und Bankdaten, sie stehen auf Rechnungen und Verträgen.", "Add company and bank details first, they appear on invoices and contracts."),
        t("Lade dein Team ein und vergib Rechte.", "Invite your team and set permissions."),
      ],
    };
  }
}

/** Creator-Konto (0178): dieselben Seiten, erklärt für eine selbständige Person; übrige Seiten wie bei der Agentur. */
export function studioGuideText(key: AgenturGuide, t: Tr): AgenturGuideText {
  switch (key) {
    case "overview": return {
      title: t("Willkommen in deinem Creator-Büro", "Welcome to your creator office"),
      text: t("Die Übersicht zeigt deinen Umsatz, offene Rechnungen und was als Nächstes fällig ist.", "The overview shows your revenue, open invoices and what is due next."),
      bullets: [
        t("Anfragen von Marken erfasst du unter „Anfragen“; aus einer Zusage wird ein Deal.", "You capture brand inquiries under “Inquiries”; a confirmed one becomes a deal."),
        t("Rechnungen schreibst du direkt an die Marke, mit Zahlungsziel und Erinnerung, wenn sie nicht zahlt.", "You invoice the brand directly, with a payment term and a reminder if it does not pay."),
        t("Die Glocke oben rechts meldet, wenn eine Marke freigibt, unterschreibt oder zahlt.", "The bell at the top right tells you when a brand approves, signs or pays."),
      ],
    };
    case "kooperationen": return {
      title: t("Deals", "Deals"),
      text: t("Jeder Deal mit Marke, Honorar und Zeitraum. Aus ihm entstehen Vertrag, Content-Freigabe und Rechnung.", "Every deal with brand, fee and period. Contract, content approval and invoice all start from it."),
      bullets: [
        t("„Neuer Deal“ legt den Deal an; Beiträge und Fristen gleich mit.", "“New deal” creates the deal; posts and deadlines along with it."),
        t("Ist ein Monat geliefert, schreibst du daraus mit einem Klick die Rechnung an die Marke.", "Once a month is delivered, you create the invoice to the brand with one click."),
      ],
    };
    case "rechnungen": return {
      title: t("Rechnungen", "Invoices"),
      text: t("Deine Rechnungen an Marken, mit Stand von offen bis bezahlt.", "Your invoices to brands, with status from open to paid."),
      bullets: [
        t("Steuer, Kleinunternehmer-Hinweis oder Reverse Charge setzt das Portal nach deinen Angaben und dem Land der Marke.", "Tax, the small-business notice or reverse charge follow your details and the brand's country."),
        t("Zahlt eine Marke nicht, erinnerst du über „Zahlung erinnern“, mit Mahnstufe und neuer Frist.", "If a brand does not pay, remind it via “Send payment reminder”, with dunning level and new deadline."),
      ],
    };
    case "content": return {
      title: t("Content", "Content"),
      text: t("Deine Videos und Beiträge je Deal, Monat und Liefergegenstand.", "Your videos and posts per deal, month and deliverable."),
      bullets: [
        t("Lade Entwürfe hoch und schick der Marke einen Freigabe-Link, ohne dass sie ein Konto braucht.", "Upload drafts and send the brand an approval link; it does not need an account."),
      ],
    };
    case "kalender": return {
      title: t("Kalender", "Calendar"),
      text: t("Fristen aus deinen Deals, Drehtermine und eigene Termine an einem Ort.", "Deadlines from your deals, shoots and your own appointments in one place."),
      bullets: [
        t("Content-Fristen erscheinen automatisch.", "Content deadlines appear automatically."),
        t("Über „Kalender abonnieren“ holst du alles in deine Kalender-App.", "With “Subscribe to calendar” you get everything into your calendar app."),
      ],
    };
    case "creator": return {
      title: t("Dein Profil", "Your profile"),
      text: t("Deine Kanäle, dein Media-Kit und deine Preisliste für Marken.", "Your channels, your media kit and your price list for brands."),
      bullets: [
        t("Das Media-Kit teilst du als Link mit Marken.", "You share the media kit with brands as a link."),
      ],
    };
    default: return agenturGuideText(key, t);
  }
}

// ── 4  Seitenhilfe im Creator-Portal ─────────────────────────────────────────

export type CreatorGuide = "overview" | "deals" | "invoices" | "wizard" | "contracts" | "chat" | "calendar" | "profile" | "bell";

export function creatorGuideText(key: CreatorGuide, t: Tr, agency: string): AgenturGuideText {
  switch (key) {
    case "overview": return {
      title: t(`Willkommen im Creator-Portal von ${agency}`, `Welcome to ${agency}'s creator portal`),
      text: t("Alles auf einen Blick: was du verdient hast, was noch kommt und was als Nächstes zu tun ist.", "Everything at a glance: what you have earned, what is still to come and what to do next."),
      bullets: [
        t("Oben stehen deine nächsten Schritte, zum Beispiel eine Rechnung, die noch fehlt.", "Your next steps are at the top, for example an invoice that is still missing."),
        t("„Erste Schritte“ führt dich durch die Einrichtung: Anschrift, Steuer, Bank, Unterschrift.", "“First steps” guides you through the setup: address, tax, bank, signature."),
        t("Die Glocke oben rechts meldet sich, wenn deine Agentur etwas von dir braucht.", "The bell at the top right tells you when your agency needs something from you."),
      ],
    };
    case "deals": return {
      title: t("Deine Deals", "Your deals"),
      text: t("Jede Kooperation mit ihren Monaten: was du abgibst, bis wann und was du daran verdienst.", "Every collaboration with its months: what you deliver, by when and what you earn."),
      bullets: [
        t("„Rechnung fehlt“ heißt: Für diesen Monat brauchst du noch eine Rechnung, sonst gibt es keine Auszahlung.", "“Invoice missing” means: you still need an invoice for this month, otherwise there is no payout."),
        t("„Deal ansehen“ zeigt Umfang, Fristen, Nutzungsrechte und Verträge.", "“View deal” shows scope, deadlines, usage rights and contracts."),
        t("„Dein Anteil“ ist dein Betrag nach der Provision deiner Agentur, netto.", "“Your share” is your amount after your agency's commission, net."),
      ],
    };
    case "invoices": return {
      title: t("Deine Rechnungen", "Your invoices"),
      text: t("Jede Rechnung mit ihrem Stand, von eingereicht bis ausgezahlt.", "Every invoice with its status, from submitted to paid out."),
      bullets: [
        t("„Rechnung einreichen“ führt dich Schritt für Schritt zur fertigen Rechnung als PDF.", "“Submit invoice” guides you step by step to the finished invoice as a PDF."),
        t("Ist das Geld angekommen, melde es über „Zahlung melden“.", "Once the money has arrived, report it via “Report payment”."),
        t("Fehlt etwas, gibt deine Agentur die Rechnung mit einem Hinweis zur Korrektur zurück.", "If something is missing, your agency returns the invoice with a note for correction."),
      ],
    };
    case "wizard": return {
      title: t("Rechnung erstellen", "Create an invoice"),
      text: t("Wähle Kooperation und Monat; Empfänger, Beträge und Steuerhinweis füllt das Portal vor.", "Choose collaboration and month; the portal pre-fills recipient, amounts and tax note."),
      bullets: [
        t("Trägst du das Budget ein, zieht das Portal die Provision deiner Agentur ab.", "If you enter the budget, the portal deducts your agency's commission."),
        t("Der Leistungszeitraum ist Pflicht: wann du die Leistung erbracht hast.", "The service period is required: when you delivered the service."),
        t("Auslagen wie Reise oder Material markierst du als provisionsfrei und hängst den Beleg an.", "Mark expenses such as travel or materials as commission-free and attach the receipt."),
      ],
    };
    case "contracts": return {
      title: t("Deine Verträge", "Your contracts"),
      text: t("Verträge zum Unterschreiben landen hier. Lies sie in Ruhe, dann unterschreibst du im Portal.", "Contracts to sign arrive here. Read them calmly, then sign in the portal."),
      bullets: [
        t("Ein neuer Vertrag meldet sich über die Glocke und per Mail.", "A new contract shows up in the bell and by email."),
        t("Deine gespeicherte Unterschrift aus dem Profil lässt sich übernehmen.", "You can use the signature saved in your profile."),
        t("Unterschriebene Verträge bleiben hier und lassen sich als PDF sichern.", "Signed contracts stay here and can be saved as a PDF."),
      ],
    };
    case "chat": return {
      title: t("Nachrichten", "Messages"),
      text: t("Dein direkter Draht zu deiner Agentur: Fragen, Dateien, kurze Absprachen.", "Your direct line to your agency: questions, files, quick arrangements."),
      bullets: [
        t("Verknüpfe eine Nachricht mit einer Rechnung, einem Vertrag oder einer Kooperation, dann weiß deine Agentur sofort, worum es geht.", "Link a message to an invoice, a contract or a collaboration so your agency knows right away what it is about."),
        t("Bilder und PDFs ziehst du hinein oder fügst sie ein.", "Drag in or paste images and PDFs."),
        t("Am Handy beginnt Enter eine neue Zeile; gesendet wird mit dem Knopf.", "On your phone, Enter starts a new line; send with the button."),
      ],
    };
    case "calendar": return {
      title: t("Dein Kalender", "Your calendar"),
      text: t("Fristen, Drehtermine und Veröffentlichungen an einem Ort, geteilt mit deiner Agentur.", "Deadlines, shoots and publications in one place, shared with your agency."),
      bullets: [
        t("Content-Fristen deiner Kooperationen erscheinen automatisch.", "Content deadlines of your collaborations appear automatically."),
        t("Eigene Termine wie Urlaub legst du selbst an.", "You add your own appointments such as holidays yourself."),
        t("Über „Kalender abonnieren“ holst du alles in deine Kalender-App.", "With “Subscribe to calendar” you get everything into your calendar app."),
      ],
    };
    case "profile": return {
      title: t("Dein Profil", "Your profile"),
      text: t("Deine Angaben für Rechnungen und Auszahlungen.", "Your details for invoices and payouts."),
      bullets: [
        t("Anschrift, Steuerstatus und Bankverbindung stehen auf deinen Rechnungen.", "Address, tax status and bank details appear on your invoices."),
        t("Änderst du die Bankverbindung, bekommst du zur Sicherheit eine Mail.", "If you change your bank details, you receive an email for security."),
        t("Deine gespeicherte Unterschrift füllt Verträge vor.", "Your saved signature pre-fills contracts."),
      ],
    };
    case "bell": return {
      title: t("Benachrichtigungen", "Notifications"),
      text: t("Was deine Agentur dir meldet und welche Aufgaben auf dich warten.", "What your agency tells you and which tasks are waiting for you."),
      bullets: [
        t("Aufgaben verschwinden von selbst, sobald sie erledigt sind.", "Tasks disappear by themselves once they are done."),
        t("Hinweise blendest du aus, wenn du sie gelesen hast.", "Hide notes once you have read them."),
        t("Welche Mails du bekommst, stellst du hier ein.", "You choose here which emails you receive."),
      ],
    };
  }
}
